import { useCallback, useState } from "react";
import { useLocationQuery } from "@/offering/api";
import { useAnalytics } from "@/lib/analytics/analytics";
import { downloadOrderReceiptPdf } from "@/fm/utils/generate_order_receipt_pdf";
import { Order } from "@/fm/data/orders";
import { MembershipCharge } from "@/offering/portal/hooks/use_membership_charges_query";
import { useReceiptDownloadEnabled } from "@/fm/hooks/use_receipt_download_enabled";

interface UseDownloadMembershipChargeReceiptParams {
  order?: Order;
  partnerId: string;
  userId: string;
  partnerName: string;
  locationId?: string;
  locationAddress?: string;
  membershipName?: string;
}

export function useDownloadMembershipChargeReceipt({
  order,
  partnerId,
  userId,
  partnerName,
  locationId,
  locationAddress,
  membershipName,
}: UseDownloadMembershipChargeReceiptParams) {
  const analytics = useAnalytics();
  const { data: location } = useLocationQuery(locationId);
  const enabled = useReceiptDownloadEnabled({
    partnerId,
    userId,
    locationId,
    order,
  });

  // Track in-flight ids in a Set so a second click on a different row doesn't
  // clear the first row's spinner when its download completes.
  const [downloadingIds, setDownloadingIds] = useState<Set<string>>(
    () => new Set()
  );

  const download = useCallback(
    async (charge: MembershipCharge) => {
      if (!order) return;
      // Recurring branch needs membershipName for the "Membership: {name}" header.
      if (!charge.isFirstCharge && !membershipName) return;

      setDownloadingIds((prev) => new Set(prev).add(charge.id));
      try {
        analytics.event(
          charge.isFirstCharge
            ? "Download Order Receipt"
            : "Download Membership Charge Receipt",
          {
            confirmationId: order.confirmationID,
            orderId: order.id,
            chargeId: charge.id,
            paymentDate: charge.paymentDate,
            totalPrice: charge.totalPrice,
            currencyCode: charge.currency,
            isFirstCharge: charge.isFirstCharge,
          },
          { onlyProviders: ["mixpanel"] }
        );

        // First charge IS the membership order — produce the same PDF the Order
        // details page does. The Order details page's useDownloadOrderReceipt
        // doesn't pass locationAddress for membership orders (no space →
        // no space.locationId → no useLocationQuery result), so we skip it here
        // too for byte-for-byte parity.
        if (charge.isFirstCharge) {
          await downloadOrderReceiptPdf({
            order,
            partnerName,
            taxRegistration: location?.taxRegistration,
          });
        } else {
          await downloadOrderReceiptPdf({
            order,
            partnerName,
            locationAddress,
            taxRegistration: location?.taxRegistration,
            recurringCharge: { charge, membershipName: membershipName! },
          });
        }
      } finally {
        setDownloadingIds((prev) => {
          const next = new Set(prev);
          next.delete(charge.id);
          return next;
        });
      }
    },
    [order, partnerName, locationAddress, membershipName, analytics, location]
  );

  return { download, downloadingIds, enabled };
}
